import { ChartType } from "@custom-types/analytics-types";
import { createSlice } from "@reduxjs/toolkit";
import { fetchCompanyStats, fetchInHouseAnalyticsDashboard, fetchProjectStats } from "@store/analytics/analytics-slice-thunk";
import { TIME_FRAMES } from "@utils/time-utils";

/** State of analytics */

/** The default time frame to show analytics in months */
export const DEFAULT_TIME_FRAME = 6;

/** Uses DEFAULT_TIME_FRAME to set the default value for the time frame  */
const DEFAULT_TIME_FRAME_VALUE = TIME_FRAMES.find(timeFrame => timeFrame.value === DEFAULT_TIME_FRAME)?.value ?? TIME_FRAMES[0].value;
const initialState = {
  // Use the default value if it exists in the list of time frames, otherwise use the first one
  selectedAnalyticsTimeFrame: DEFAULT_TIME_FRAME_VALUE,
  companyStats: null,
  projectStats: {},
  fetching: {
    isFetchingCompanyStats: false,
    isFetchingProjectStats: false
  },
  projectOverview: {
    selectedTimeFrame: DEFAULT_TIME_FRAME_VALUE,
    selectedChartType: ChartType.projectSpheres
  },
  inHouseDashboard: {
    summary: null,
    fetchingStatus: "uninitialized"
  }
};

/**
 * Slice to access state of loaded groups
 */
const analyticsSlice = createSlice({
  name: "analytics",
  initialState,
  reducers: {
    /**
     * Stores the selected time frame.
     */
    setSelectedAnalyticsTimeFrame(state, action) {
      state.selectedAnalyticsTimeFrame = action.payload ?? DEFAULT_TIME_FRAME;
    },
    /** Sets the selected project chart time frame */
    setSelectedProjectChartTimeFrame(state, action) {
      state.projectOverview.selectedTimeFrame = action.payload;
    },
    /** Sets the selected project chart type */
    setSelectedProjectChartType(state, action) {
      state.projectOverview.selectedChartType = action.payload;
    },
    /** Resets the analytics store to its initial state. */
    resetAnalyticsState: () => initialState
  },
  extraReducers(builder) {
    builder.addCase(fetchCompanyStats.pending, state => {
      state.fetching.isFetchingCompanyStats = true;
    }).addCase(fetchCompanyStats.fulfilled, (state, action) => {
      state.fetching.isFetchingCompanyStats = false;
      state.companyStats = action.payload;
    }).addCase(fetchCompanyStats.rejected, state => {
      state.fetching.isFetchingCompanyStats = false;
    }).addCase(fetchProjectStats.pending, state => {
      state.fetching.isFetchingProjectStats = true;
    }).addCase(fetchProjectStats.fulfilled, (state, action) => {
      state.fetching.isFetchingProjectStats = false;
      state.projectStats[action.payload.projectId] = action.payload.stats;
    }).addCase(fetchProjectStats.rejected, state => {
      state.fetching.isFetchingProjectStats = false;
    }).addCase(fetchInHouseAnalyticsDashboard.pending, state => {
      state.inHouseDashboard.fetchingStatus = "pending";
    }).addCase(fetchInHouseAnalyticsDashboard.fulfilled, (state, action) => {
      state.inHouseDashboard.summary = action.payload;
      state.inHouseDashboard.fetchingStatus = "succeeded";
    }).addCase(fetchInHouseAnalyticsDashboard.rejected, state => {
      state.inHouseDashboard.fetchingStatus = "rejected";
    });
  }
});
export const {
  setSelectedAnalyticsTimeFrame,
  setSelectedProjectChartTimeFrame,
  setSelectedProjectChartType,
  resetAnalyticsState
} = analyticsSlice.actions;
export const analyticsReducer = analyticsSlice.reducer;